import { getMarketHours } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { useEffect, useState } from 'react';

export const AWAIT_LIVE_PRICE_TIMEOUT_MS = 3000;

export interface UseIsAwaitingLivePriceParams {
  hasLiveTick: boolean;
  marketHours?: ReturnType<typeof getMarketHours>;
  enabled?: boolean;
}

/**
 * Returns `true` while the caller should show a skeleton in place of the price,
 * i.e. when the market is currently in a live session (pre / core / after),
 * no websocket tick has arrived yet, and the fallback timeout hasn't elapsed.
 *
 * Once any of those conditions flips, returns `false` so callers can render
 * the real price. Prevents a "flash of last close" before the first WS tick.
 */
export const useIsAwaitingLivePrice = ({
  hasLiveTick,
  marketHours = getMarketHours(),
  enabled = true,
}: UseIsAwaitingLivePriceParams): boolean => {
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const isInLiveSession =
    marketHours.isPreMarket ||
    marketHours.isMarketHours ||
    marketHours.isAfterMarket;

  const shouldWait = enabled && isInLiveSession && !hasLiveTick && !hasTimedOut;

  useEffect(() => {
    if (!shouldWait) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setHasTimedOut(true);
    }, AWAIT_LIVE_PRICE_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [shouldWait]);

  return shouldWait;
};
